import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import './Settings.css';

const TIMEZONE_OPTIONS = [
  'Asia/Kathmandu', 'Asia/Kolkata', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Dubai',
  'Asia/Singapore', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo', 'Australia/Sydney', 'Pacific/Auckland', 'UTC',
];

const TAG_PRESETS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#0ea5e9',
];

function Settings({ token, handleLogout, username }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Preferences state
  const [defaultPriority, setDefaultPriority] = useState('Medium');
  const [timezone, setTimezone] = useState('Asia/Kathmandu');
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefMsg, setPrefMsg] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');

  // Tags state
  const [allTags, setAllTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_PRESETS[0]);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    fetchProfile();
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/profile`, { headers: authHeaders() });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      setProfile(data);
      setDefaultPriority(data.defaultPriority || 'Medium');
      setTimezone(data.timezone || 'Asia/Kathmandu');
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await fetch(`${API_URL}/tasks/tags`, { headers: authHeaders() });
      if (res.ok) setAllTags(await res.json());
    } catch { /* ignore */ }
  };

  const savePreferences = async () => {
    setPrefSaving(true);
    setPrefMsg('');
    try {
      const res = await fetch(`${API_URL}/profile/preferences`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ defaultPriority, timezone }),
      });
      if (res.ok) {
        setPrefMsg('✅ Preferences saved!');
      } else {
        const data = await res.json();
        setPrefMsg(`❌ ${data.error}`);
      }
    } catch {
      setPrefMsg('❌ Failed to save preferences');
    } finally {
      setPrefSaving(false);
      setTimeout(() => setPrefMsg(''), 4000);
    }
  };

  const changePassword = async () => {
    setPwError('');
    setPwMsg('');

    if (!currentPassword || !newPassword) {
      setPwError('Please fill in all password fields');
      return;
    }
    if (newPassword.length < 6) {
      setPwError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match');
      return;
    }

    setPwSaving(true);
    try {
      const res = await fetch(`${API_URL}/profile/password`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwMsg('✅ Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPwError(data.error || 'Failed to change password');
      }
    } catch {
      setPwError('Failed to connect to server');
    } finally {
      setPwSaving(false);
      setTimeout(() => { setPwMsg(''); setPwError(''); }, 5000);
    }
  };

  const createTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/tasks/tags`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: newTagName.trim(), color: newTagColor }),
      });
      if (res.ok) {
        const tag = await res.json();
        setAllTags(prev => [...prev, tag]);
        setNewTagName('');
        setNewTagColor(TAG_PRESETS[0]);
      }
    } catch (err) {
      console.error('Create tag error:', err);
    }
  };

  const deleteTag = async (tagId) => {
    try {
      await fetch(`${API_URL}/tasks/tags/${tagId}`, { method: 'DELETE', headers: authHeaders() });
      setAllTags(prev => prev.filter(t => t.id !== tagId));
    } catch (err) {
      console.error('Delete tag error:', err);
    }
  };

  if (loading) return <div className="settings-loading">Loading Settings...</div>;

  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account, preferences, and tags.</p>
      </div>

      <div className="settings-grid">
        {/* ── Profile Card ── */}
        <div className="settings-card profile-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">👤</span>
            <h2>Profile</h2>
          </div>
          <div className="profile-avatar">
            {(username || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="profile-info">
            <div className="profile-username">{username}</div>
            <div className="profile-badge">
              {profile?.isAdmin ? '👑 Admin' : '👤 User'}
            </div>
            <div className="profile-meta">Member since {memberSince}</div>
          </div>
        </div>

        {/* ── Preferences Card ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">⚙️</span>
            <h2>Preferences</h2>
          </div>

          <div className="settings-field">
            <label>Default Priority</label>
            <select
              className="settings-select"
              value={defaultPriority}
              onChange={e => setDefaultPriority(e.target.value)}
            >
              <option value="High">🔴 High</option>
              <option value="Medium">🟡 Medium</option>
              <option value="Low">🟢 Low</option>
            </select>
            <span className="settings-field-hint">New tasks will use this priority by default.</span>
          </div>

          <div className="settings-field">
            <label>Timezone</label>
            <select
              className="settings-select"
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
            >
              {TIMEZONE_OPTIONS.map(tz => (
                <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <button className="settings-save-btn" onClick={savePreferences} disabled={prefSaving}>
            {prefSaving ? 'Saving...' : 'Save Preferences'}
          </button>
          {prefMsg && <div className="settings-msg">{prefMsg}</div>}
        </div>

        {/* ── Notifications Card ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">🔔</span>
            <h2>Notifications</h2>
          </div>
          
          <div className="settings-field">
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Enable browser push notifications to get real-time alerts when your tasks are due in 24 hours or 1 hour.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                className="settings-save-btn" 
                onClick={async () => {
                  const { requestNotificationPermission } = await import('../utils/NotificationUtils');
                  const granted = await requestNotificationPermission();
                  if (granted) alert('Notifications enabled!');
                  else alert('Notifications denied. Please enable them in your browser settings.');
                }}
              >
                Enable Notifications
              </button>
              <button 
                className="settings-save-btn" 
                style={{ background: 'var(--glass-bg)', color: 'var(--text-primary)', border: '1px solid var(--glass-border)' }}
                onClick={async () => {
                  const { sendBrowserNotification } = await import('../utils/NotificationUtils');
                  sendBrowserNotification("Test Notification", { body: "Real-time alerts are working correctly! 🚀" });
                }}
              >
                Test Alert
              </button>
            </div>
          </div>
        </div>

        {/* ── Change Password Card ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">🔒</span>
            <h2>Change Password</h2>
          </div>

          <div className="settings-field">
            <label>Current Password</label>
            <input
              type="password"
              className="settings-input"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
            />
          </div>

          <div className="settings-field">
            <label>New Password</label>
            <input
              type="password"
              className="settings-input"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
            />
          </div>

          <div className="settings-field">
            <label>Confirm New Password</label>
            <input
              type="password"
              className="settings-input"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              onKeyDown={e => { if (e.key === 'Enter') changePassword(); }}
            />
          </div>

          {pwError && <div className="settings-error">{pwError}</div>}
          {pwMsg && <div className="settings-msg">{pwMsg}</div>}

          <button className="settings-save-btn" onClick={changePassword} disabled={pwSaving}>
            {pwSaving ? 'Changing...' : 'Change Password'}
          </button>
        </div>

        {/* ── Tags Management Card ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">🏷</span>
            <h2>Manage Tags</h2>
          </div>

          <div className="settings-tag-create">
            <input
              type="text"
              className="settings-input"
              placeholder="New tag name..."
              value={newTagName}
              onChange={e => setNewTagName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createTag(); }}
            />
            <div className="settings-color-row">
              {TAG_PRESETS.map(c => (
                <button
                  key={c}
                  className={`settings-color-btn ${newTagColor === c ? 'active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setNewTagColor(c)}
                />
              ))}
            </div>
            <button className="settings-save-btn" onClick={createTag} disabled={!newTagName.trim()}>
              Create Tag
            </button>
          </div>

          <ul className="settings-tag-list">
            {allTags.length === 0 && (
              <li className="settings-tag-empty">No tags yet. Create one above!</li>
            )}
            {allTags.map(tag => (
              <li key={tag.id} className="settings-tag-item">
                <span className="settings-tag-dot" style={{ background: tag.color }} />
                <span className="settings-tag-name">{tag.name}</span>
                <button className="settings-tag-delete" onClick={() => deleteTag(tag.id)}>✕</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Settings;
